import * as Sentry from '@sentry/browser';
import { BootFlags, buildData, getBootFlag, getTenantNamespace } from '@/app-config';
import { chronosphereLogsClient } from '../logs';
import { AppErrorSeverity } from './AppError';
import { isFetchError } from './FetchError';
import { isNotAuthenticatedError } from './NotAuthenticatedError';
import { isNotFoundError } from './NotFoundError';
import { isOfflineError } from './OfflineError';

/**
 *
 * * From one of the spec authors:
 * > This error means that ResizeObserver was not able to deliver all observations within a single animation frame.
 * > It is benign (your site will not break)."
 * https://stackoverflow.com/questions/49384120/resizeobserver-loop-limit-exceeded/50387233#50387233
 *
 * If we start doing anything animation heavy, we may want to disable this catch-all and catch these
 * exceptions individually for instrumentation. It's most likely happening because of the use-resize-observer
 * hook, which is wrapped around our uPlot charts.
 */
const resizeObserverErrors = [
  'ResizeObserver loop limit exceeded', // Chrome
  'ResizeObserver loop completed with undelivered notifications.', // Firefox equivalent
];

const notAuthenticatedErrors = ['Not authenticated'];

const ignoredErrors = [
  ...resizeObserverErrors,
  ...notAuthenticatedErrors,
  /Resource of type `classic-dashboard` with slug (.)* not found/i,
];

export function initializeErrorTracking() {
  // Don't use Sentry for local dev or if it's been disabled
  if (isSentryDisabled()) {
    return;
  }

  try {
    const tenant = getTenantNamespace();

    // TODO: Proper follow-up for this would be to grab this from some data (e.g.
    // an environment variable) passed to the UI container, but for now, just
    // set based on tenant name
    // See https://app.shortcut.com/chronosphere/story/15112/add-release-and-environment-metadata-to-sentry-reporting
    let environment = 'production';
    if (tenant === 'meta') {
      environment = 'meta';
    } else if (tenant.startsWith('rc-')) {
      environment = 'rc';
    }

    Sentry.init({
      dsn: 'https://ef4c59adbbe0434b966b631b1e0570d2@o465684.ingest.sentry.io/5478737',
      environment,
      release: buildData.gitTag,
      initialScope: {
        tags: {
          tenant,
        },
      },
      beforeSend: (event) => {
        try {
          submitSentryEventToChronosphereLogs(event);
        } catch (_error) {
          // noop
        }
        return event;
      },
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.captureConsoleIntegration({ levels: ['error'] }),
        Sentry.httpClientIntegration(),
      ],

      // Sample rate for transactions
      // https://docs.sentry.io/platforms/javascript/configuration/sampling/#configuring-the-transaction-sample-rate
      tracesSampleRate: 0.05,

      ignoreErrors: ignoredErrors,
      denyUrls: [
        // Chrome extensions
        /extensions\//i,
        /^chrome:\/\//i,
        /^chrome-extension:\/\//i,
        /\/otlp\/v1\/traces/i,
      ],
    });
  } catch (err) {
    // Don't break the app if this fails for some reason
    console.error('Error initializing error reporting', err);
  }
}

// Allows logging additional contextual information about an error
export interface ErrorLogContext {
  contexts: {
    react?: {
      componentStack: string;
    };
    storage?: {
      storageSizes: {
        localStorage: { [key: string]: number };
        sessionStorage: { [key: string]: number };
      };
    };
  };
}

/**
 * Set a severity level for errors to make it easier to filter alerts both in Sentry and M3:
 */
export const getErrorSeverityLevel = (error: unknown): AppErrorSeverity => {
  const ignoreLevel = 'warning';

  if (error instanceof Error) {
    if (isNotFoundError(error)) return ignoreLevel;
    if (isOfflineError(error)) return ignoreLevel;
    if (isNotAuthenticatedError(error)) return ignoreLevel;
    if (error.name === 'ChunkLoadError') return ignoreLevel;
    if (isFetchError(error)) return ignoreLevel;
  }

  return 'error';
};

/**
 * Basic utility for capturing and logging errors. Does not do any sort of analytics tracking, just reports errors.
 */
export function logError(error: unknown, context?: ErrorLogContext) {
  // Don't do error reporting to Sentry for local dev or if it's been disabled
  if (isSentryDisabled()) {
    console.error(error);
    return;
  }

  Sentry.captureException(error, {
    level: getErrorSeverityLevel(error),
    ...context,
  });
}

export interface GlobalErrorLogContext {
  user: {
    id: string;
    internal: boolean;
    isSysAdmin: boolean;
  };
  graphql: {
    buildVersion: string;
  };
  fullstoryUrl?: string;
}

/**
 * NOTE: Stop, you probably don't need to use this. It's handled for you already in Cloud in the `AnalyticsProvider`.
 *
 * Adds some contextual information to error logging that isn't available until the user has logged into the app. This
 * will be used whenever logging errors via `logError`.
 */
export function addContextToErrorLogging(context: GlobalErrorLogContext) {
  // Nothing to do in dev or if Sentry is disabled
  if (isSentryDisabled()) {
    return;
  }

  try {
    Sentry.setUser(context.user);
    Sentry.setContext('graphql', context.graphql);
    // The context set on the user is stored in sentry, but not available when doing queries.  Adding similar information
    // as tags should make it queryable
    Sentry.setTag('internal_user', context.user.internal || false);
    Sentry.setTag('sysadmin_user', context.user.isSysAdmin || false);
    Sentry.setTag('fullstoryUrl', context.fullstoryUrl);
  } catch (error) {
    logError(error);
  }
}

function isSentryDisabled() {
  const sentryMode = getBootFlag(BootFlags.SENTRY_MODE);
  if (process.env.NODE_ENV === 'development' || sentryMode === 'disabled') {
    return true;
  }
  return false;
}

/**
 * Function that sends the Sentry event to Chronosphere Logs.
 */
function submitSentryEventToChronosphereLogs(event: Sentry.Event) {
  // Gets the message from the event, which is the error message or the arguments of the error.
  const message = getEventMessage(event);
  const eventPayload = {
    source: 'sentry',
    message,
    level: event.level ?? 'error',
    sentry_event_id: event.event_id,
    tags: event.tags,
    request: event.request,
    happened_at: event.timestamp ? new Date(event.timestamp * 1000).toISOString() : undefined,
    contexts: event.contexts,
    release: event.release,
    extra: event.extra,
  };

  chronosphereLogsClient.submitLog(eventPayload);
}

function getEventMessage(event: Sentry.Event): string | undefined {
  if (typeof event.message === 'string' && event.message.trim()) {
    return event.message;
  }

  const fallback = formatExtraArguments(event.extra?.arguments);
  if (fallback && fallback.trim()) {
    return fallback;
  }

  return event.message;
}

function formatExtraArguments(extraArguments: unknown): string | undefined {
  if (!extraArguments) {
    return undefined;
  }

  if (typeof extraArguments === 'string') {
    return extraArguments;
  }

  if (Array.isArray(extraArguments)) {
    const parts = extraArguments
      .map((argument) => formatExtraArgument(argument))
      .filter((part): part is string => Boolean(part));
    return parts.length ? parts.join(' ') : undefined;
  }

  return formatExtraArgument(extraArguments);
}

function formatExtraArgument(argument: unknown): string | undefined {
  if (argument === null || argument === undefined) {
    return undefined;
  }

  if (argument instanceof Error) {
    return argument.message || argument.name;
  }

  if (
    typeof argument === 'string' ||
    typeof argument === 'number' ||
    typeof argument === 'boolean' ||
    typeof argument === 'bigint'
  ) {
    return String(argument);
  }

  try {
    return JSON.stringify(argument);
  } catch (_error) {
    return String(argument);
  }
}
